import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { FaUser, FaEnvelope, FaLock, FaGlobe, FaBuilding, FaMapMarkerAlt } from 'react-icons/fa';
import './ProfilePage.css';
import { Link } from 'react-router-dom';  // Add this import at the top

function ProfilePage() {

    
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { 
    user, 
    updateUserProfile, 
    updateEmail, 
    updatePassword, 
    EmailAuthProvider, 
    reauthenticateWithCredential 
  } = useAuth();
  
  // User profile states
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: '',
    username: '',  // Add this
    bio: '',
    website: '',
    company: '',
    location: '',
  });
  
  // Password update states
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    fetchUserData();
    fetchProjects();
  }, [user]);

  const fetchUserData = async () => {
    if (!user) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setProfileData({
          displayName: user.displayName || '',
          bio: userData.bio || '',
          website: userData.website || '',
          company: userData.company || '',
          location: userData.location || '',
        });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchProjects = async () => {
    if (!user) return;
    
    try {
      const q = query(
        collection(db, 'projects'),
        where('userId', '==', user.uid)
      );
      
      const querySnapshot = await getDocs(q);
      const projectsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setProjects(projectsData);
    } catch (error) {
      setError('Error fetching projects: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update email
      await updateEmail(user, newEmail);
      setSuccess('Email updated successfully!');
      setIsEditing(false);
    } catch (error) {
      setError('Failed to update email: ' + error.message);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, newPassword);
      setSuccess('Password updated successfully!');
      setIsEditing(false);
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setError('Failed to update password: ' + error.message);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteDoc(doc(db, 'projects', projectId));
        setProjects(projects.filter(project => project.id !== projectId));
        setSuccess('Project deleted successfully');
      } catch (error) {
        setError('Error deleting project: ' + error.message);
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await updateUserProfile(profileData);
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      setError('Failed to update profile: ' + error.message);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-sidebar">
          <div className="profile-avatar">
            <FaUser size={60} />
            <h2>{profileData.displayName || 'User'}</h2>
            <p className="email">{user?.email}</p>
          </div>
          
          <button 
            className={`edit-button ${isEditing ? 'active' : ''}`}
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Cancel Editing' : 'Edit Profile'}
          </button>
        </div>

        <div className="profile-content">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {isEditing ? (
            <div className="settings-section">
              <form onSubmit={handleProfileUpdate} className="profile-form">
                <h3>Profile Information</h3>
                <div className="form-group">
                  <FaUser />
                  <input
                    type="text"
                    placeholder="Display Name"
                    value={profileData.displayName}
                    onChange={(e) => setProfileData({...profileData, displayName: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <textarea
                    placeholder="Bio"
                    value={profileData.bio}
                    onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <FaGlobe />
                  <input
                    type="url"
                    placeholder="Website"
                    value={profileData.website}
                    onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <FaBuilding />
                  <input
                    type="text"
                    placeholder="Company"
                    value={profileData.company}
                    onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <FaMapMarkerAlt />
                  <input
                    type="text"
                    placeholder="Location"
                    value={profileData.location}
                    onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                  />
                </div>

                <button type="submit" className="save-button">Save Profile</button>
              </form>

              {/* Keep existing email and password update forms */}
            </div>
          ) : (
            <div className="profile-info">
              <div className="info-section">
                <h3>About</h3>
                <p>{profileData.bio || 'No bio provided'}</p>
              </div>

              {profileData.website && (
                <div className="info-item">
                  <FaGlobe />
                  <a href={profileData.website} target="_blank" rel="noopener noreferrer">
                    {profileData.website}
                  </a>
                </div>
              )}

              {profileData.company && (
                <div className="info-item">
                  <FaBuilding />
                  <span>{profileData.company}</span>
                </div>
              )}

              {profileData.location && (
                <div className="info-item">
                  <FaMapMarkerAlt />
                  <span>{profileData.location}</span>
                </div>
              )}
            </div>
          )}

          <div className="projects-section">
            <h2>My Projects</h2>
            <div className="projects-grid">
  {projects.map(project => (
    <Link 
      key={project.id} 
      to={`/project/${project.id}`}
      className="project-card"
    >
      <div className="project-header">
        <h3>{project.title || 'Untitled Project'}</h3>
        <button 
          className="delete-button"
          onClick={(e) => {
            e.preventDefault(); // Prevent navigation when clicking delete
            handleDeleteProject(project.id);
          }}
        >
          Delete
        </button>
      </div>
      <p className="project-date">
        {new Date(project.createdAt).toLocaleDateString()}
      </p>
      <div className="preview">{project.lastMessage}</div>
    </Link>
  ))}
</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;